import {getOrganization} from './api';
import mapboxgl from 'mapbox-gl';
import {calculatePolygonCentroid, STALE_LOCATION_THRESHOLD_MS} from '@ruta/shared';
import { STONE, GREEN } from '@/lib/brandColors';
import { applyProximity } from './geocodeProximity';
import { reportMapboxApiCall } from '@/lib/integrationUsage';

export { STALE_LOCATION_THRESHOLD_MS };

/**
 * Check if a location timestamp is stale (no update in 2 minutes)
 */
export function isLocationStale(timestamp: string | Date): boolean {
  const locationTime = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = Date.now();
  const timeSinceUpdate = now - locationTime.getTime();
  return timeSinceUpdate > STALE_LOCATION_THRESHOLD_MS;
}

/**
 * Get marker styles for a crew member location based on clock status and staleness
 * Returns color and opacity for the marker
 */
export function getCrewLocationMarkerStyles(isClocked: boolean, timestamp: string | Date): {
  backgroundColor: string;
  opacity: number;
  isStale: boolean;
} {
  const stale = isLocationStale(timestamp);
  
  if (!isClocked) {
    // Off clock - gray, full opacity
    return {
      backgroundColor: STONE[400], // gray
      opacity: 1,
      isStale: false,
    };
  }
  
  if (stale) {
    // Clocked in but stale location - gray, semi-transparent
    return {
      backgroundColor: STONE[400], // stone-400
      opacity: 0.5,
      isStale: true,
    };
  }
  
  // Clocked in with fresh location - green, full opacity
  return {
    backgroundColor: GREEN[600], // green-500
    opacity: 1,
    isStale: false,
  };
}

// Service area styling constants - using green for all service area displays
const SERVICE_AREA_COLORS = {
  fill: GREEN[600],      // Green 500 from Tailwind
  outline: GREEN[600],   // Green 600 from Tailwind
} as const;

export interface MapCoordinates {
  lng: number;
  lat: number;
}

export interface MetroAreaZoom {
  center: MapCoordinates;
  zoom: number;
}

// Mapbox access token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

/**
 * Extract zip code from an address string
 */
export function extractZipCode(address: string): string | null {
  if (!address) return null;

  // Match 5-digit zip codes or 5+4 format
  const zipMatch = address.match(/\b\d{5}(?:-\d{4})?\b/);
  return zipMatch ? zipMatch[0].split('-')[0] : null;
}

export interface GeocodeResult extends MapCoordinates {
  placeName: string;
}

/**
 * Geocode an address using Mapbox Geocoding API.
 *
 * Pass `options.proximity` (the org's service-region center) to bias results
 * so a street typed without a city resolves locally instead of out-of-state
 * (RUT-3034). Omit it to keep the un-biased national search.
 */
export async function geocodeAddress(
  address: string,
  options?: { proximity?: MapCoordinates | null },
): Promise<GeocodeResult | null> {
  if (!MAPBOX_TOKEN || !address) {
    console.warn('Mapbox token not available or address empty');
    return null;
  }

  try {
    const params = new URLSearchParams({ country: 'US', access_token: MAPBOX_TOKEN });
    applyProximity(params, options?.proximity);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?${params.toString()}`,
    );

    if (!response.ok) {
      console.error('Geocoding request failed:', response.status, response.statusText);
      throw new Error('Geocoding request failed');
    }

    void reportMapboxApiCall();

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      console.warn('No geocoding results found for address:', address);
      return null;
    }

    // Log the first result for debugging
    const feature = data.features[0];
    console.log('Geocoding result for', address, ':', {
      place_name: feature.place_name,
      center: feature.center,
      geometry: feature.geometry.coordinates,
      relevance: feature.relevance
    });

    const [lng, lat] = feature.geometry.coordinates;
    return { lng, lat, placeName: feature.place_name };
  } catch (error) {
    console.error('Geocoding error for address:', address, error);
    return null;
  }
}

/**
 * Reverse geocode coordinates to get an address using Mapbox Geocoding API
 */
export async function reverseGeocodeCoordinates(lng: number, lat: number): Promise<string | null> {
  if (!MAPBOX_TOKEN) {
    console.warn('Mapbox token not available');
    return null;
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?country=US&types=address&access_token=${MAPBOX_TOKEN}`,
    );

    if (!response.ok) {
      console.error('Reverse geocoding request failed:', response.status, response.statusText);
      throw new Error('Reverse geocoding request failed');
    }

    void reportMapboxApiCall();

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      console.warn('No reverse geocoding results found for coordinates:', lng, lat);
      return null;
    }

    // Get the first result (most relevant)
    const feature = data.features[0];
    console.log('Reverse geocoding result for', lng, lat, ':', {
      place_name: feature.place_name,
      relevance: feature.relevance
    });

    return feature.place_name;
  } catch (error) {
    console.error('Reverse geocoding error for coordinates:', lng, lat, error);
    return null;
  }
}

/**
 * Get appropriate zoom level for metro area based on place type
 */
export function getMetroZoomLevel(placeType?: string): number {
  // Metro area zoom levels - reduced to show larger metropolitan areas
  const zoomLevels = {
    'place': 8,         // City level - zoom out to show metro area
    'postcode': 9,      // ZIP code level - zoom out to show surrounding area
    'district': 8,      // District level - metro area view
    'neighborhood': 10, // Neighborhood level - broader area view
    'address': 9,       // Address level - show surrounding metro
    'region': 7,        // State/region level
    'country': 4,       // Country level
  };

  return zoomLevels[placeType as keyof typeof zoomLevels] || 8; // Default to metro area level
}

/**
 * Get metro area coordinates and zoom level from company profile
 */
export async function getCompanyMetroArea(organization?: any): Promise<MetroAreaZoom | null> {
  try {
    // If organization not provided, fall back to API call (legacy behavior)
    let org = organization;
    if (!org) {
      const orgResponse = await getOrganization();
      org = orgResponse.organization;
    }

    if (!org) {
      console.warn('No organization found');
      return null;
    }

    // Try company address first, fallback to shop address
    const address = org.companyAddress || org.shopAddress;

    if (!address) {
      console.warn('No company address found in organization profile');
      return null;
    }

    console.log('Geocoding company address:', address);

    // Geocode the address
    const coordinates = await geocodeAddress(address);

    if (!coordinates) {
      console.warn('Could not geocode company address');
      return null;
    }

    // Extract zip code to determine appropriate zoom level
    // Use lower zoom levels to show entire metro areas
    const zipCode = extractZipCode(address);
    const zoom = zipCode ? 10 : 9; // Metro area zoom levels - much more zoomed out

    return {
      center: coordinates,
      zoom,
    };
  } catch (error) {
    console.error('Error getting company metro area:', error);
    return null;
  }
}

/**
 * Default fallback coordinates (center of US)
 */
export const DEFAULT_MAP_CENTER: MapCoordinates = {
  lng: -98.5795,
  lat: 39.8283,
};

export const DEFAULT_MAP_ZOOM = 4;

/**
 * Calculate the distance between two geographic points using the Haversine formula
 * @returns Distance in meters
 */
export function haversineDistance(
  point1: [number, number], // [lng, lat]
  point2: [number, number]  // [lng, lat]
): number {
  const R = 6371000; // Earth's radius in meters
  const lat1 = point1[1] * Math.PI / 180;
  const lat2 = point2[1] * Math.PI / 180;
  const deltaLat = (point2[1] - point1[1]) * Math.PI / 180;
  const deltaLng = (point2[0] - point1[0]) * Math.PI / 180;

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Convert a distance in meters to a pixel radius at zoom level 20.
 * Used with Mapbox exponential base-2 zoom interpolation so circles
 * represent real-world size at every zoom level.
 */
export function metersToPixelRadiusAtZoom20(meters: number): number {
  const EARTH_CIRCUMFERENCE_M = 40075016.686;
  return meters * (Math.pow(2, 20) * 512) / EARTH_CIRCUMFERENCE_M;
}

/**
 * Check if a point is within a specified distance of a polygon
 * @param point The point to check [lng, lat]
 * @param polygonCoordinates The polygon boundary coordinates
 * @param thresholdMeters The distance threshold in meters
 * @returns true if the point is within the threshold distance of the polygon centroid
 */
export function isPointNearPolygon(
  point: [number, number],
  polygonCoordinates: number[][],
  thresholdMeters: number = 5
): boolean {
  const centroid = calculatePolygonCentroid(polygonCoordinates);
  const distance = haversineDistance(point, centroid);
  return distance <= thresholdMeters;
}

/**
 * Service Area overlay utilities for customer maps
 */
export interface ServiceArea {
  id: string;
  name: string;
  type: string;
  coordinates: number[][];
  isActive: boolean;
}

/**
 * Add service area layers to a map instance
 */
export function addServiceAreaLayers(map: mapboxgl.Map): void {
  if (!map) return;

  try {
    // Remove existing layers if they exist
    if (map.getLayer('service-areas-labels')) {
      map.removeLayer('service-areas-labels');
    }
    if (map.getLayer('service-areas-outline')) {
      map.removeLayer('service-areas-outline');
    }
    if (map.getLayer('service-areas-fill')) {
      map.removeLayer('service-areas-fill');
    }
    if (map.getSource('service-areas')) {
      map.removeSource('service-areas');
    }

    // Create service areas source
    map.addSource('service-areas', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    // Add fill layer (background)
    map.addLayer({
      id: 'service-areas-fill',
      type: 'fill',
      source: 'service-areas',
      paint: {
        'fill-color': SERVICE_AREA_COLORS.fill,
        'fill-opacity': 0.15,
      },
    });

    // Add outline layer (border)
    map.addLayer({
      id: 'service-areas-outline',
      type: 'line',
      source: 'service-areas',
      paint: {
        'line-color': SERVICE_AREA_COLORS.outline,
        'line-width': 3,
        'line-opacity': 0.8,
      },
    });

    // Add label layer for service area names
    map.addLayer({
      id: 'service-areas-labels',
      type: 'symbol',
      source: 'service-areas',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.05,
        'text-anchor': 'center',
      },
      paint: {
        'text-color': SERVICE_AREA_COLORS.outline,
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
        'text-halo-blur': 1,
      },
    });
  } catch (error) {
    console.error('Error adding service area layers:', error);
  }
}

/**
 * Convert service areas to GeoJSON features
 */
export function serviceAreasToFeatures(areas: ServiceArea[]): any[] {
  console.log('[serviceAreasToFeatures] Input areas:', areas.length);
  const filtered = areas.filter(area => {
    const hasCoords = area.coordinates && area.coordinates.length >= 3;
    if (!hasCoords) {
      console.log('[serviceAreasToFeatures] Filtered out:', area.name, 'coords:', area.coordinates?.length);
    }
    return hasCoords;
  });
  console.log('[serviceAreasToFeatures] After filter:', filtered.length);
  if (filtered.length > 0) {
    console.log('[serviceAreasToFeatures] Sample coords:', filtered[0].name, filtered[0].coordinates?.slice(0, 3));
  }
  return filtered.map(area => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [area.coordinates], // Note: coordinates wrapped in array for GeoJSON
      },
      properties: {
        id: area.id,
        name: area.name,
        type: area.type,
      },
    }));
}

/**
 * Update service areas display on a map
 */
export function updateServiceAreasDisplay(map: mapboxgl.Map, areas: ServiceArea[]): void {
  if (!map) return;

  try {
    const source = map.getSource('service-areas') as mapboxgl.GeoJSONSource;
    if (!source) {
      console.warn('Service areas source not found, cannot update display');
      return;
    }

    const features = serviceAreasToFeatures(areas);
    console.log('[updateServiceAreasDisplay] Setting GeoJSON with', features.length, 'features');
    if (features.length > 0) {
      console.log('[updateServiceAreasDisplay] Sample feature geometry:', JSON.stringify(features[0].geometry).slice(0, 200));
    }

    source.setData({
      type: 'FeatureCollection',
      features,
    });
    console.log('[updateServiceAreasDisplay] Source data set successfully');
  } catch (error) {
    console.error('Error updating service areas display:', error);
  }
}
